// Pure and dependency-free: the core uses it on the main thread and worker addons bundle it,
// so a worker never has to load the engine.
export interface HsSphereCullingTree {
  spheres: Float64Array;
  indices: Uint32Array;
  bounds: Float64Array;
  nodes: Int32Array;
}

export type HsSphereCullingWorkerResponse = { ok: true; tree: HsSphereCullingTree } | { ok: false; error: string };

export interface HsCullingPlane {
  normal: { x: number; y: number; z: number };
  offset: number;
}

const LEAF_SIZE: number = 32;
const NODE_STRIDE: number = 4;
const BOUNDS_STRIDE: number = 8;

export function hsBuildSphereCullingTree(spheres: Float64Array): HsSphereCullingTree {
  if (spheres.length % 4 !== 0) {
    throw new Error('Culling sphere snapshot must contain four values per object');
  }
  const valid: number[] = [];
  for (let index: number = 0; index < spheres.length / 4; index++) {
    const offset: number = index * 4;
    if (
      Number.isFinite(spheres[offset]) &&
      Number.isFinite(spheres[offset + 1]) &&
      Number.isFinite(spheres[offset + 2]) &&
      Number.isFinite(spheres[offset + 3]) &&
      spheres[offset + 3] >= 0
    ) {
      valid.push(index);
    }
  }
  const indices: Uint32Array = Uint32Array.from(valid);
  const bounds: number[] = [];
  const nodes: number[] = [];
  const build = (start: number, end: number): number => {
    const node: number = nodes.length / NODE_STRIDE;
    nodes.push(-1, -1, start, end);
    const minima: number[] = [Infinity, Infinity, Infinity, Infinity];
    const maxima: number[] = [-Infinity, -Infinity, -Infinity, -Infinity];
    for (let cursor: number = start; cursor < end; cursor++) {
      const offset: number = indices[cursor] * 4;
      for (let axis: number = 0; axis < 4; axis++) {
        minima[axis] = Math.min(minima[axis], spheres[offset + axis]);
        maxima[axis] = Math.max(maxima[axis], spheres[offset + axis]);
      }
    }
    bounds.push(...minima.slice(0, 3), ...maxima.slice(0, 3), minima[3], maxima[3]);
    if (end - start > LEAF_SIZE) {
      let axis: number = 0;
      for (let candidate: number = 1; candidate < 3; candidate++) {
        if (maxima[candidate] - minima[candidate] > maxima[axis] - minima[axis]) {
          axis = candidate;
        }
      }
      indices
        .subarray(start, end)
        .sort((left: number, right: number) => spheres[left * 4 + axis] - spheres[right * 4 + axis]);
      const middle: number = (start + end) >>> 1;
      nodes[node * NODE_STRIDE] = build(start, middle);
      nodes[node * NODE_STRIDE + 1] = build(middle, end);
    }
    return node;
  };
  if (indices.length > 0) {
    build(0, indices.length);
  }
  return { spheres, indices, bounds: Float64Array.from(bounds), nodes: Int32Array.from(nodes) };
}

export function hsMarkSphereCullingTree(
  tree: HsSphereCullingTree,
  planes: readonly HsCullingPlane[],
  marked: Uint8Array,
  fallback: Uint8Array
): number {
  marked.set(fallback);
  let count: number = 0;
  const visit = (node: number): void => {
    const bound: number = node * BOUNDS_STRIDE;
    let inside: boolean = true;
    for (const plane of planes) {
      const { x, y, z } = plane.normal;
      const maximum: number =
        x * tree.bounds[bound + (x >= 0 ? 3 : 0)] +
        y * tree.bounds[bound + (y >= 0 ? 4 : 1)] +
        z * tree.bounds[bound + (z >= 0 ? 5 : 2)] +
        plane.offset;
      const minimum: number =
        x * tree.bounds[bound + (x >= 0 ? 0 : 3)] +
        y * tree.bounds[bound + (y >= 0 ? 1 : 4)] +
        z * tree.bounds[bound + (z >= 0 ? 2 : 5)] +
        plane.offset;
      // Widen node tests for rounding; boundary spheres still use the exact original arithmetic.
      const margin: number =
        64 *
        Number.EPSILON *
        (Math.abs(x) * Math.max(Math.abs(tree.bounds[bound]), Math.abs(tree.bounds[bound + 3])) +
          Math.abs(y) * Math.max(Math.abs(tree.bounds[bound + 1]), Math.abs(tree.bounds[bound + 4])) +
          Math.abs(z) * Math.max(Math.abs(tree.bounds[bound + 2]), Math.abs(tree.bounds[bound + 5])) +
          Math.abs(plane.offset) +
          tree.bounds[bound + 7] +
          1);
      if (maximum + margin < -tree.bounds[bound + 7]) {
        return;
      }
      if (!(minimum - margin >= -tree.bounds[bound + 6])) {
        inside = false;
      }
    }
    const offset: number = node * NODE_STRIDE;
    const start: number = tree.nodes[offset + 2];
    const end: number = tree.nodes[offset + 3];
    if (inside) {
      count += end - start;
      for (let cursor: number = start; cursor < end; cursor++) {
        marked[tree.indices[cursor]] = 1;
      }
    } else if (tree.nodes[offset] >= 0) {
      visit(tree.nodes[offset]);
      visit(tree.nodes[offset + 1]);
    } else {
      for (let cursor: number = start; cursor < end; cursor++) {
        const index: number = tree.indices[cursor];
        const sphere: number = index * 4;
        let visible: boolean = true;
        for (const plane of planes) {
          const distance: number =
            plane.normal.x * tree.spheres[sphere] +
            plane.normal.y * tree.spheres[sphere + 1] +
            plane.normal.z * tree.spheres[sphere + 2] +
            plane.offset;
          if (distance < -tree.spheres[sphere + 3]) {
            visible = false;
            break;
          }
        }
        if (visible) {
          marked[index] = 1;
          count++;
        }
      }
    }
  };
  if (tree.nodes.length > 0) {
    visit(0);
  }
  return count;
}
